import React, { useMemo, useCallback } from "react";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import { getRouteApi } from "@tanstack/react-router";
// UI
import { Menu, Button, Modal } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCalendar, IconArrowLeft } from "@tabler/icons-react";
import { SpecificDatesSelect } from "./specific-dates-select";
// utils
import { useQuickPicks } from "./date-range-picker-helpers";
import {
  timePeriodMatches,
  type TimePeriod,
} from "@utils/chart-data-conversions/timelines";

const route = getRouteApi("/_authenticated/app/players/$playerId/insights");

export function DateRangePicker(): React.ReactElement {
  const quickPickSections = useQuickPicks();

  const { startDate, endDate, granularity } = route.useSearch();

  const { t } = useTranslation();

  const [
    isSpecificDateSelectOpen,
    { open: openSpecificDateSelect, close: closeSpecificDateSelect },
  ] = useDisclosure(false);

  const navigate = route.useNavigate();

  const handleSubmit = useCallback(
    async (tp: TimePeriod) => {
      await navigate({
        search: {
          startDate: tp.start.toFormat("yyyy-MM-dd"),
          endDate: tp.end.toFormat("yyyy-MM-dd"),
          granularity: tp.granularity,
        },
      });

      closeSpecificDateSelect();
    },
    [navigate, closeSpecificDateSelect],
  );

  const timePeriodFromSearchParams = useMemo<TimePeriod>(() => {
    return {
      start: DateTime.fromISO(startDate),
      end: DateTime.fromISO(endDate),
      granularity,
    };
  }, [startDate, endDate, granularity]);

  const slectedTimeLabel = useMemo(() => {
    const allQuickSelections = quickPickSections
      .map((sec) => sec.options)
      .flat();

    const matchingQuickSelection = allQuickSelections.find((qs) => {
      return timePeriodMatches(qs.period, timePeriodFromSearchParams);
    });
    if (matchingQuickSelection) {
      return matchingQuickSelection.label;
    }

    const { start, end } = timePeriodFromSearchParams;
    return `${start.toLocaleString()} - ${end.toLocaleString()}`;
  }, [timePeriodFromSearchParams, quickPickSections]);

  return (
    <>
      <Menu shadow="md">
        <Menu.Target>
          <Button size="sm" radius={4} leftSection={<IconCalendar size={16} />}>
            {slectedTimeLabel}
          </Button>
        </Menu.Target>

        <Menu.Dropdown>
          {quickPickSections.map((qpSection, sectionIdx) => {
            return (
              <React.Fragment key={sectionIdx}>
                {sectionIdx > 0 ? <Menu.Divider /> : null}

                {qpSection.options.map((qp, qpIdx) => {
                  const isSelected = timePeriodMatches(
                    qp.period,
                    timePeriodFromSearchParams,
                  );

                  return (
                    <Menu.Item
                      key={qpIdx}
                      rightSection={
                        isSelected ? <IconArrowLeft size={14} /> : null
                      }
                      onClick={async () => {
                        await handleSubmit(qp.period);
                      }}
                    >
                      {qp.label}
                    </Menu.Item>
                  );
                })}
              </React.Fragment>
            );
          })}

          <Menu.Divider />

          <Menu.Item onClick={openSpecificDateSelect}>
            {t("dateRangePicker.specificDates")}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <Modal
        opened={isSpecificDateSelectOpen}
        onClose={closeSpecificDateSelect}
        title={t("playerInsights.configureSection.datePickerTitle")}
      >
        <SpecificDatesSelect onSubmit={handleSubmit} />
      </Modal>
    </>
  );
}
